import React, { useState, useEffect,useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, Box, LinearProgress } from "@mui/material";
import { setFormData, nextStep, previousStep, setSessionId, setApplicationId } from "../../redux/actions";
import { renderQuestion } from "../../helpers/renderer";
import { RefinanceIcon } from "../../assets/svgIconsList";
import { ReactComponent as PurchaseIcon } from "../../assets/Purchase.svg";
import miltaryimage from "../../assets/miltary.png";
import "./Questionnaire.css";
import { saveStepdata } from "../../services/formService";
import { useLocation, useNavigate } from "react-router-dom";
import { extractUrlParameters, preserveAllUrlParams, restoreDynamicParamsFromStorage } from "../../utils/urlParams";

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  return re.test(String(email).toLowerCase());
};

const validatePhoneNumber = (phoneNumber) => {
  // Accepts formats: (XXX) XXX-XXXX, +1 (XXX) XXX-XXXX, XXX-XXX-XXXX, etc.
  const re = /^(?:\+1[.\-\s]?)?(?:\(?\d{3}\)?[.\-\s]?)\d{3}[.\-\s]?\d{4}$/;
  return re.test(String(phoneNumber).trim());
};

const validateCreditScore = (score) => {
  return score >= 300 && score <= 850;
};

const validateWithRegex = (value, regex, errorMessage) => {
  return regex.test(String(value).trim()) && value.trim().length > 0;
};

const nameRegex = /^[a-zA-Z\s'-]+$/;
const nameErrorMessage = "Invalid name";

const Questionnaire = ({ questions, onFormSubmit }) => {
  const currentStep = useSelector((state) => state.currentStep);
  const formData = useSelector((state) => state.formData);
  const dispatch = useDispatch();
  const location=useLocation()
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("language");
    return saved === "Spanish" ? "Spanish" : "English";
  });
  const [errors, setErrors] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showMilitaryThankYou, setShowMilitaryThankYou] = useState(false);
  const [hasShownMilitaryThankYou, setHasShownMilitaryThankYou] =
    useState(false);

  const savedStepsRef=useRef(new Set())
  const autoAdvanceRef=useRef(null)
  const leadTrackedRef = useRef(false)

  const sessionId = useSelector((state) => state?.sessionId);
  const applicationId = useSelector((state) => state?.applicationId);
  const crmLeadId = useSelector((state) => state?.crmLeadId);
  const formType = useSelector((state) => state.formType);


  useEffect(() => {
    const img = new Image();
    img.src = miltaryimage;
  }, []);

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "language") {
        setLang(e.newValue === "Spanish" ? "Spanish" : "English");
      }
    };

    const handleLangChanged = (e) => {
      const next = e?.detail;
      setLang(next === "Spanish" ? "Spanish" : "English");
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("langChanged", handleLangChanged);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("langChanged", handleLangChanged);
    };
  }, []);

  useEffect(() => {
    const currentSectionId = questions?.[currentStep]?.id;
    const autoAdvanceToken = `${currentStep}:${formData?.isMilitary || ""}`;
    if (
      formType === "refinance" &&
      currentSectionId === "military" &&
      formData?.isMilitary === "No" &&
      currentStep < questions.length - 1 &&
      autoAdvanceRef.current !== autoAdvanceToken
    ) {
      autoAdvanceRef.current = autoAdvanceToken;
      dispatch(nextStep());
    }
  }, [dispatch, formData?.isMilitary, formType, currentStep, questions]);

  useEffect(()=>{
    const saveCurrentStep=async()=>{
      if (currentStep===0) return
      
      const searchParams = new URLSearchParams(location.search);
      const { registeredParams, dynamicParameters } = extractUrlParameters(searchParams);
      
      const larFromUrl = registeredParams.lar || searchParams.get("lar");
      const utmSourceFromUrl = registeredParams.utm_source || searchParams.get("utm_source");
      const utmTermFromUrl = registeredParams.utm_term || searchParams.get("utm_term");
      const utmCampaignFromUrl = registeredParams.utm_campaign || searchParams.get("utm_campaign");

      const lar = larFromUrl || localStorage.getItem("lar") || "undefined";
      const utm_source =
        utmSourceFromUrl || localStorage.getItem("utm_source") || "undefined";
      if (utmTermFromUrl) {
        localStorage.setItem("utm_term", utmTermFromUrl);
      }
      const utm_term = utmTermFromUrl || localStorage.getItem("utm_term") || "";
      if (utmCampaignFromUrl) {
        localStorage.setItem("utm_campaign", utmCampaignFromUrl);
      }
      const utm_campaign = utmCampaignFromUrl || localStorage.getItem("utm_campaign") || "";
      const lang = localStorage.getItem("language") || "English";
      try{
        const stepData={
          session_id:sessionId,
          application_id:applicationId,
          current_step:currentStep,
          form_data:formData,
          form_type:formType,
          lar:lar,
          utm_source,
          utm_term,
          utm_campaign,
          language:lang,
          status:"in_progress",
          crm_lead_id:crmLeadId || "",
          dynamic_parameters: dynamicParameters
        }
       
        const response=await saveStepdata(stepData)
        if (response){
          if(!sessionId&& response.session_id){
              dispatch(setSessionId(response.session_id));
          }
          if(!applicationId && response.application_id){
               dispatch(setApplicationId(response.application_id));
          }
          if(response.data_miss==="true"){
            dispatch(setSessionId(response.session_id))
            dispatch(setApplicationId(response.session_id))
          }
        }

        savedStepsRef.current.add(currentStep)
      }catch(error){
        console.error(`Error saving step ${currentStep}:`,error)
      }
    }

    const timeoutId = setTimeout(saveCurrentStep, 500);
    return () => clearTimeout(timeoutId);
  },[currentStep,crmLeadId,questions])


  const resetDependentFields = (parentKey, parentValue) => {
    questions.forEach((section) => {
      section.questions.forEach((question) => {
        if (
          question.conditional &&
          question.conditional.questionKey === parentKey &&
          formData[question.key] !== undefined
        ) {
          if (formData[parentKey] !== parentValue) {
            dispatch(setFormData(question.key, ""));
          }
          if (parentKey === "propertyType" && parentValue !== "Multi Family") {
            dispatch(setFormData("numberOfUnits", 1));
          }
        }
      });
    });
  };

  const selectedValueTriggersConditional = (questionKey, selectedValue) => {
    let triggersConditional = false;
    questions.forEach((section) => {
      section.questions.forEach((question) => {
        if (
          question.conditional &&
          question.conditional.questionKey === questionKey &&
          question.conditional.value === selectedValue
        ) {
          triggersConditional = true;
        }
      });
    });
    return triggersConditional;
  };

  const areAllQuestionsAnswered = () => {
    return stepQuestions.every((question) => {
      if (!shouldRenderQuestion(question)) {
        return true;
      }
      if (question.type === "addressAutoComplete") {
        // For addressAutoComplete, allow continue if:
        // 1. No address fields have been started, OR
        // 2. At least city OR address is provided (don't require all fields)
        const hasAnyAddressField = formData.address || formData.city || formData.state || formData.county || formData.zipCode;
        if (!hasAnyAddressField) {
          // No address fields started, allow continue
          return true;
        }
        // If any address field has been started, allow continue if at least city OR address is provided
        const hasCityOrAddress = (formData.city && formData.city !== "") || (formData.address && formData.address !== "");
        return hasCityOrAddress;
      }
      if (
        question.type === "cityAutoComplete" &&
        formData.daysToPurchase === "I'm under contract"
      ) {
        // When under contract, AddressAutoComplete is rendered
        // Allow continue if:
        // 1. No address fields have been started, OR
        // 2. At least city OR address is provided (don't require all fields)
        const hasAnyAddressField = formData.address || formData.city || formData.state || formData.county || formData.zipCode;
        if (!hasAnyAddressField) {
          // No address fields started, allow continue
          return true;
        }
        // If any address field has been started, allow continue if at least city OR address is provided
        const hasCityOrAddress = (formData.city && formData.city !== "") || (formData.address && formData.address !== "");
        return hasCityOrAddress;
      }
      return (
        formData[question.key] !== undefined && formData[question.key] !== ""
      );
    });
  };

  const stepNeedsContinueButton = () => {
    if (!stepQuestions.length) return false;

    for (const question of stepQuestions) {
      if (!shouldRenderQuestion(question)) continue;

      const textInputTypes = [
        "text",
        "amount",
        "number",
        "percentage",
        "cityAutoComplete",
        "addressAutoComplete",
      ];
      if (
        textInputTypes.includes(question.type) 
     
      ) {
        return true;
      }

      if (question.type === "multipleChoice") {
      const currentValue = formData[question.key];
      const hasSelection = Array.isArray(currentValue) && currentValue.length > 0;
      if (hasSelection) {
        return true;
      }
    }
    }

    return false;
  };

  useEffect(() => {
    if (!lastUpdated) return;

    const { key, value } = lastUpdated;

    // Show military thank you message when user selects "Yes" for military service (only first time)
    if (key === "isMilitary" && value === "Yes") {
      setShowMilitaryThankYou(true);
      setHasShownMilitaryThankYou(true);
      // Auto-hide the message after 3 seconds
      setTimeout(() => {
        setShowMilitaryThankYou(false);
      }, 3000);
    }

    if (areAllQuestionsAnswered()) {
      const currentQuestion = questions[currentStep]?.questions.find(
        (q) => q.key === key
      );

      if (currentQuestion) {
        const textInputTypes = [
          "text",
          "amount",
          "number",
          "percentage",
          "cityAutoComplete",
          "addressAutoComplete",
        ];

        const isTextInput = textInputTypes.includes(currentQuestion.type);
        const isCheckbox = currentQuestion.type === "multipleChoice";

        const triggersConditional = selectedValueTriggersConditional(
          key,
          value
        );

        const canAutoAdvance =
          !isTextInput &&
          !isCheckbox &&
          !errors[key] &&
          !triggersConditional &&
          currentStep < questions.length - 1 &&
          !stepNeedsContinueButton();

        if (canAutoAdvance) {
          dispatch(nextStep());
        }
      }
    }

    setLastUpdated(null);
  }, [formData, lastUpdated, currentStep, errors]);

  const handleChange = (key, value) => {
    let validationError = null;

    if (key === "email" && !validateEmail(value)) {
      validationError = "Invalid email address";
    }

    if (key === "phoneNumber") {
      if (!String(value || "").trim()) {
        validationError = "Phone number is required";
      } else if (!validatePhoneNumber(value)) {
        validationError = "Invalid phone number";
      }
    }

    if (key === "creditScore" && !validateCreditScore(value)) {
      validationError = "Credit score must be between 300 and 850";
    }

    if ((key === "firstName" || key === "lastName") && !validateWithRegex(value, nameRegex)) {
      validationError = nameErrorMessage;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [key]: validationError,
    }));

    if (key === "propertyType" && value !== "Multi Family") {
      dispatch(setFormData("numberOfUnits", 1));
    }
    if (key === "isMilitary" && value === "No") {
      dispatch(setFormData("vaFirstTimeUse", "No"));
    }
    if (key === "daysToPurchase") {
      dispatch(setFormData("address", ""));
      dispatch(setFormData("city", ""));
      dispatch(setFormData("state", ""));
      dispatch(setFormData("county", ""));
      dispatch(setFormData("zipCode", ""));
    }

    dispatch(setFormData(key, value));

    resetDependentFields(key, value);

    setLastUpdated({ key, value });

    const currentSectionId = questions?.[currentStep]?.id;
    const autoAdvanceToken = `${currentStep}:${key}:${value}`;
    if (
      formType === "refinance" &&
      currentSectionId === "military" &&
      key === "isMilitary" &&
      value === "No" &&
      currentStep < questions.length - 1 &&
      autoAdvanceRef.current !== autoAdvanceToken
    ) {
      autoAdvanceRef.current = autoAdvanceToken;
      dispatch(nextStep());
    }
  };

  const handleSubmit = () => {
    const hasErrors = Object.values(errors).some((error) => error !== null);
    if (!hasErrors && areAllQuestionsAnswered()) {
      const searchParams = new URLSearchParams(location.search);
      const larFromUrl = searchParams.get("lar");
      const sourceFromUrl = searchParams.get("utm_source");
      const kwFromUrl = searchParams.get("utm_term");
      const campaignFromUrl = searchParams.get("utm_campaign");

      const lar =
        larFromUrl || localStorage.getItem("lar") || "admin";
      const source =
        sourceFromUrl || localStorage.getItem("utm_source") || "direct";
      if (kwFromUrl) {
        localStorage.setItem("utm_term", kwFromUrl);
      }
      if (campaignFromUrl) {
        localStorage.setItem("utm_campaign", campaignFromUrl);
      }

      localStorage.setItem("lar", lar);
      localStorage.setItem("utm_source", source);

      localStorage.setItem("formData", JSON.stringify(formData));
      onFormSubmit();
    }
  };

  const shouldRenderQuestion = (question) => {
    if (!question.conditional) {
      return true;
    }
    const { questionKey, value } = question.conditional;
    const parentValue = formData[questionKey];
    if (Array.isArray(parentValue)) {
      return parentValue.includes(value);
    }
    return parentValue === value;
  };

  const handleCloseMilitaryThankYou = () => {
    setShowMilitaryThankYou(false);
  };

  const currentSection = questions[currentStep] || {};
  const stepQuestions = currentSection.questions || [];
  const sectionLabel = currentSection.label || "";

  const progress = ((currentStep + 1) / questions.length) * 100;

  const hasErrors = Object.values(errors).some((error) => error !== null);

// Track Lead 
  const trackMetaLeadIfNeeded = () => {
    const currentSectionId = questions?.[currentStep]?.id;
    if (currentSectionId !== "personalInformation") return;

    const storageKey = `meta_pixel_lead_fired_${
      sessionId || applicationId || "default"
    }`;
    if (leadTrackedRef.current || sessionStorage.getItem(storageKey) === "1") {
      return;
    }

    if (typeof window.fbq === "function") {
      window.fbq("track", "Lead");
    }
    leadTrackedRef.current = true;
    sessionStorage.setItem(storageKey, "1");
  };

  const handleContinue = () => {
    if (!areAllQuestionsAnswered() || hasErrors) return;
    trackMetaLeadIfNeeded();
    dispatch(nextStep());
  };

  // Track Lead on Submit

  const needsContinueButton = stepNeedsContinueButton();

  const formTypeLabelByLang = {
    English: {
      purchase: "Purchase",
      refinance: "Refinance",
      appointment: "Appointment",
    },
    Spanish: {
      purchase: "Compra",
      refinance: "Refinanciar",
      appointment: "Cita",
    },
  };

  const formTypeLabel = formTypeLabelByLang[lang]?.[formType] || formType;
  const buttonLabelsByLang = {
    English: {
      continue: "Continue",
      submit: "Submit",
      back: "Back",
    },
    Spanish: {
      continue: "Continuar",
      submit: "Enviar",
      back: "Atrás",
    },
  };

  const buttonLabels = buttonLabelsByLang[lang] || buttonLabelsByLang.English;

  // Helper function to get dynamic label for cityAutoComplete questions
  const getQuestionLabel = (question) => {
    if (question.type === 'cityAutoComplete') {
      const isUnderContract = formData.daysToPurchase === "I'm under contract";
      if (isUnderContract) {
        return lang === "Spanish" ? "Ingrese su dirección" : "Please enter your Property Address";
      } else {
        return lang === "Spanish" ? "Ingrese su ciudad" : "Please enter your city";
      }
    }
    return question.label;
  };

  return (
    <div className="form-container">
      {/* Military Thank You Modal/Overlay */}
      {showMilitaryThankYou && (
        <div className="military-thank-you-overlay">
          <div className="military-thank-you-box">
            <div className="military-thank-you-content">
              <img
                src={miltaryimage}
                alt="Military Appreciation"
                className="military-thank-you-image"
              />
              <button
                className="military-thank-you-close"
                onClick={handleCloseMilitaryThankYou}
              >
                {buttonLabels.continue}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="questionnaire-layout">
        <div className="question-column">
          <div className="main-question">{sectionLabel}</div>
        </div>

        <div className="options-column">
          {stepQuestions.map(
            (question) =>
              shouldRenderQuestion(question) && (
                <div key={question.type === 'cityAutoComplete' ? `${question.key}-${formData.daysToPurchase || 'default'}` : question.key} className="options-box">
                  <div
                    className="sub-question"
                    style={
                      question.key === "creditInquiryAuthorization"
                        ? { fontSize: "0.8rem" } // Smaller font size for the last question
                        : {}
                    }
                  >
                    {getQuestionLabel(question)}
                  </div>
                  <div className="options">
                    {renderQuestion(question, handleChange, formData, errors)}
                  </div>
                </div>
              )
          )}

          <div className="navigation-buttons">
            {currentStep < questions.length - 1 ? (
              needsContinueButton && (
                <button
                  onClick={handleContinue}
                  disabled={!areAllQuestionsAnswered() || hasErrors}
                  className="continue-button"
                >
                  {buttonLabels.continue}
                </button>
              )
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!areAllQuestionsAnswered() || hasErrors}
                className="submit-button"
              >
                {buttonLabels.submit}
              </button>
            )}
            {currentStep > 0 ? (
              <button
                onClick={() => dispatch(previousStep())}
                className="back-button"
              >
                {buttonLabels.back}
              </button>
            ) : (
              <button
                onClick={() => {
                  // Preserve all URL parameters (both registered and dynamic)
                  const searchParams = preserveAllUrlParams(location.search);
                  
                  // Restore dynamic parameters from localStorage if not in URL
                  restoreDynamicParamsFromStorage(searchParams);
                  
                  // Ensure required parameters have defaults if missing
                  if (!searchParams.has("lar")) {
                    const larFromStorage = localStorage.getItem("lar") || "admin";
                    searchParams.set("lar", larFromStorage);
                  }
                  if (!searchParams.has("utm_source")) {
                    const sourceFromStorage = localStorage.getItem("utm_source") || "direct";
                    searchParams.set("utm_source", sourceFromStorage);
                  }
                  
                  // Handle optional parameters from localStorage if not in URL
                  if (!searchParams.has("utm_term")) {
                    const kwFromStorage = localStorage.getItem("utm_term");
                    if (kwFromStorage) {
                      searchParams.set("utm_term", kwFromStorage);
                    }
                  }
                  
                  if (!searchParams.has("utm_campaign")) {
                    const campaignFromStorage = localStorage.getItem("utm_campaign");
                    if (campaignFromStorage) {
                      searchParams.set("utm_campaign", campaignFromStorage);
                    }
                  }
                  
                  if (!searchParams.has("utm_type")) {
                    const utmTypeFromStorage = localStorage.getItem("utm_type");
                    if (utmTypeFromStorage) {
                      searchParams.set("utm_type", utmTypeFromStorage);
                    }
                  }

                  navigate(`${process.env.REACT_APP_BASE_URL}?${searchParams.toString()}`);
                }}
                className="back-button"
              >
                {buttonLabels.back}
              </button>
            )}
          </div>
        </div>

        <div className="progress-column">
          <div className="progress-container">
            <div className="form-type">
              {formType === "purchase" ? (
                <>
                  <PurchaseIcon height="28" width="30" />
                  <p style={{ marginLeft: "0.5rem" }}>{formTypeLabel}</p>
                </>
              ) : (
                <>
                  <RefinanceIcon fill={"#ffffff"} height="30" width="30" />
                  <p style={{ marginLeft: "0.5rem" }}>{formTypeLabel}</p>
                </>
              )}   
            </div>
            {/* <div className="approx-time">Approx. 3min</div> */}
            <Box className="circular-progress-container">
              <CircularProgress
                variant="determinate"
                value={100}
                size={280}
                thickness={2}
                sx={{
                  color: "#f2f2f2",
                  "& .MuiCircularProgress-circle": {
                    strokeLinecap: "round",
                  },
                }}
              />
              <CircularProgress
                variant="determinate"
                value={progress}
                size={280}
                thickness={2}
                sx={{
                  color: "#00634a",
                  position: "absolute",
                  left: 0,
                  "& .MuiCircularProgress-circle": {
                    strokeLinecap: "round",
                  },
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div className="progress-percentage">
                  {Math.round(progress)}%
                </div>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              className="linear-progress-container"
              sx={{
                width: "100%",
                height: 10,
                borderRadius: 5,
                backgroundColor: "#f2f2f2",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "#00634a",
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;
